import { DatePicker } from "@/components/DatePicker";
import { useMemo, useState } from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { unwrap } from "@tern/api-client";
import { Check, Flag, LayoutList, Network, Plus, Trash2, X } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { ProjectEmptyState, ProjectToolbar } from "@/components/ProjectLayout";
import { Markdown, MarkdownEditor } from "@/components/Markdown";
import { Avatar } from "@/components/PropertyIcons";
import { ProgressRing, TaskStatusIcon } from "@/components/TaskProps";
import { MilestoneCanvas } from "@/components/MilestoneCanvas";
import { MilestoneTaskPanel } from "@/components/MilestoneTaskPanel";
import { api, errorMessage } from "@/lib/api";
import { liveMilestones, liveTasks, useProject, useProjectStore, type Milestone, type ProjectData } from "@/lib/store";
import { TASK_STATUSES, descendants, daysBetween, isDone, milestoneAtRisk, milestoneProgress, milestoneStatus, milestoneTasks, taskProgress, todayISO } from "@/lib/tasks";
import { toast } from "@/lib/toast";
import { useViews, type ViewSettings } from "@/lib/views";
import { ProjectCrumb } from "./ProjectIssues";
import { ProjectLoading, useLoadProject } from "./ProjectHome";

const route = getRouteApi("/$ws/project/$key/milestones");
const STATUS_COLOR = { upcoming: "#8b8d91", active: "var(--accent)", overdue: "#eb5757", completed: "#4cb782" };

export function ProjectMilestonesPage() {
  const { ws, key } = route.useParams();
  useLoadProject(ws, key);
  const d = useProject(ws, key);
  return d ? <MilestoneWorkspace key={d.project.id} d={d} /> : <ProjectLoading ws={ws} kkey={key} />;
}

function MilestoneWorkspace({ d }: { d: ProjectData }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ws = d.slug;
  const viewKey = `project:${d.project.id}:milestones`;
  const saved = useViews((s) => s.views[viewKey]?.milestoneDiagram);
  const setView = useViews((s) => s.set);
  const options = saved ?? {};
  const layout = options.layout ?? "diagram";
  const [editing, setEditing] = useState<Milestone | "new" | null>(null);
  const [selected, setSelected] = useState<{ kind: "task" | "milestone"; id: string } | null>(null);
  const canEdit = !!d.project.role;
  const list = useMemo(() => liveMilestones(d), [d]);
  const tasks = useMemo(() => {
    const all = liveTasks(d);
    const milestoneIds = options.milestoneId ? new Set(all.filter((task) => task.milestone_id === options.milestoneId).flatMap((task) => [task.id, ...descendants(d, task.id).map((child) => child.id)])) : null;
    return all.filter((task) => (!milestoneIds || milestoneIds.has(task.id)) && (!options.status || task.status === options.status) && (!options.leadId || (options.leadId === "none" ? !task.lead_id : task.lead_id === options.leadId)));
  }, [d, options.milestoneId, options.status, options.leadId]);
  const update = (patch: NonNullable<ViewSettings["milestoneDiagram"]>) => {
    setView(viewKey, { milestoneDiagram: { ...options, ...patch } });
    setSelected(null);
  };
  const openTask = (id: string) => void navigate({ to: "/$ws/task/$id", params: { ws, id } });
  const selMilestone = selected?.kind === "milestone" ? d.milestones[selected.id] : undefined;
  const selTask = selected?.kind === "task" ? d.tasks[selected.id] : undefined;
  return <>
    <PageHeader crumbs={[<ProjectCrumb key="p" ws={ws} d={d} />, t("nav.milestone")]}
      right={canEdit && <button className="btn btn-primary h-8 text-[13px]" onClick={() => setEditing("new")}><Plus className="h-3.5 w-3.5" />{t("milestone.new")}</button>} />
    <ProjectToolbar right={<div className="flex gap-0.5 rounded-md bg-sidebar p-0.5">
      {(["diagram", "list"] as const).map((value) => <button key={value} type="button" aria-pressed={layout === value} className={`flex h-7 items-center gap-1.5 rounded px-2 text-[12px] ${layout === value ? "bg-elevated text-fg shadow" : "text-muted"}`} onClick={() => update({ layout: value })}>{value === "diagram" ? <Network className="h-3.5 w-3.5" /> : <LayoutList className="h-3.5 w-3.5" />}{t(`diagram.${value}`)}</button>)}
    </div>}>
      {layout === "diagram" ? <>
        <select className="btn h-8 max-w-[200px] text-[12px]" aria-label={t("diagram.milestoneFilter")} value={options.milestoneId ?? ""} onChange={(e) => update({ milestoneId: e.target.value })}><option value="">{t("diagram.allSchedule")}</option>{list.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
        <select className="btn h-8 max-w-[140px] text-[12px]" aria-label={t("diagram.statusFilter")} value={options.status ?? ""} onChange={(e) => update({ status: e.target.value })}><option value="">{t("diagram.allStatuses")}</option>{TASK_STATUSES.map((status) => <option key={status} value={status}>{t(`taskStatus.${status}`)}</option>)}</select>
        <select className="btn h-8 max-w-[150px] text-[12px]" aria-label={t("diagram.leadFilter")} value={options.leadId ?? ""} onChange={(e) => update({ leadId: e.target.value })}><option value="">{t("diagram.allLeads")}</option><option value="none">{t("task.noLead")}</option>{d.members.map((member) => <option key={member.user_id} value={member.user_id}>{member.name}</option>)}</select>
        {(options.milestoneId || options.status || options.leadId) && <button type="button" className="btn btn-ghost h-7 w-7 justify-center p-0" aria-label={t("diagram.clearFilters")} onClick={() => update({ milestoneId: "", status: "", leadId: "" })}><X className="h-3.5 w-3.5" /></button>}
      </> : <span className="text-[13px] text-muted">Milestone · {list.length}</span>}
    </ProjectToolbar>
    <div className="flex min-h-0 flex-1 overflow-hidden">
      {layout === "diagram" ? <MilestoneCanvas d={d} tasks={tasks} milestones={options.milestoneId ? list.filter((m) => m.id === options.milestoneId) : list} selectedId={selTask && !selTask.deleted_at ? selTask.id : null} onSelect={(id) => setSelected(id ? { kind: "task", id } : null)} onOpenTask={openTask} onSelectMilestone={(id) => setSelected({ kind: "milestone", id })} /> : list.length ? <MilestoneList d={d} list={list} onSelect={(id) => setSelected({ kind: "milestone", id })} selected={selMilestone?.id ?? null} /> : <ProjectEmptyState title={t("milestone.emptyTitle")} body={t("milestone.emptyBody")} />}
      {selTask && !selTask.deleted_at && <MilestoneTaskPanel d={d} task={selTask} onClose={() => setSelected(null)} onSelect={(id) => setSelected({ kind: "task", id })} onOpen={() => openTask(selTask.id)} />}
      {selMilestone && !selMilestone.deleted_at && <aside className="w-[340px] shrink-0 overflow-y-auto border-l border-border p-4"><MilestonePanel d={d} m={selMilestone} canEdit={canEdit} onEdit={() => setEditing(selMilestone)} onClose={() => setSelected(null)} onOpenTask={openTask} /></aside>}
    </div>
    <MilestoneDialog d={d} target={editing} onClose={() => setEditing(null)} />
  </>;
}

function statusLabel(t: (k: string, o?: Record<string, unknown>) => string, m: Milestone) {
  const s = milestoneStatus(m);
  const days = daysBetween(todayISO(), m.target_date);
  const dday = s === "completed" ? "" : days === 0 ? t("milestone.dueToday") : days > 0 ? `D-${days}` : `D+${-days}`;
  return { s, dday };
}

function MilestoneList({ d, list, onSelect, selected }: { d: ProjectData; list: Milestone[]; onSelect: (id: string) => void; selected: string | null }) {
  const { t, i18n } = useTranslation();
  const open = list.filter((m) => !m.completed_at);
  const done = list.filter((m) => m.completed_at);
  const Row = ({ m }: { m: Milestone }) => {
    const { s, dday } = statusLabel(t, m);
    const prog = milestoneProgress(d, m.id);
    const tasks = milestoneTasks(d, m.id);
    return (
      <button className={`flex h-[40px] w-full items-center gap-3 border-b border-border/60 px-3 text-left text-[13px] hover:bg-hover ${selected === m.id ? "bg-active" : ""}`} onClick={() => onSelect(m.id)}>
        <Flag className="h-3.5 w-3.5 shrink-0" style={{ color: STATUS_COLOR[s] }} />
        <span className="min-w-0 flex-1 truncate font-medium">{m.name}</span>
        {milestoneAtRisk(d, m) && <span className="text-warning" title={t("milestone.atRisk")}>⚠</span>}
        <span className="w-[100px] text-[12px] text-muted">{new Date(m.target_date + "T00:00:00").toLocaleDateString(i18n.language, { month: "short", day: "numeric" })}</span>
        <span className={`w-[60px] text-[12px] ${s === "overdue" ? "text-danger" : "text-muted"}`}>{dday}</span>
        <span className="w-[90px] text-[12px] text-muted">{t(`milestone.status.${s}`)}</span>
        <span className="flex w-[80px] items-center gap-1.5 text-[12px] text-muted"><ProgressRing ratio={prog?.ratio ?? null} />{prog ? `${Math.round(prog.ratio * 100)}%` : "—"}</span>
        <span className="w-[60px] text-[12px] text-muted">{tasks.length} {t("nav.tasks").toLowerCase()}</span>
        <span className="w-[70px] text-[12px] text-muted">{prog ? `${prog.completed}/${prog.total}` : "—"}</span>
      </button>
    );
  };
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="flex h-8 items-center gap-3 border-b border-border px-3 text-[11px] font-medium uppercase tracking-wide text-faint">
        <span className="w-3.5" /><span className="flex-1">{t("task.name")}</span><span className="w-[100px]">{t("task.targetDate")}</span><span className="w-[60px]">D-day</span><span className="w-[90px]">{t("issue.status")}</span><span className="w-[80px]">{t("task.progress")}</span><span className="w-[60px]">{t("nav.tasks")}</span><span className="w-[70px]">{t("nav.issues")}</span>
      </div>
      {open.map((m) => <Row key={m.id} m={m} />)}
      {done.length > 0 && <div className="px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-faint">{t("milestone.status.completed")}</div>}
      {done.map((m) => <Row key={m.id} m={m} />)}
    </div>
  );
}

function MilestonePanel({ d, m, canEdit, onEdit, onClose, onOpenTask }: { d: ProjectData; m: Milestone; canEdit: boolean; onEdit: () => void; onClose: () => void; onOpenTask: (id: string) => void }) {
  const { t, i18n } = useTranslation();
  const { s, dday } = statusLabel(t, m);
  const prog = milestoneProgress(d, m.id);
  const tasks = milestoneTasks(d, m.id);
  const complete = async (completed: boolean) => {
    try {
      const saved = unwrap(await api.PATCH("/api/milestones/{id}", { params: { path: { id: m.id } }, body: { completed } }));
      useProjectStore.getState().applyMilestone(saved);
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };
  const remove = async () => {
    try {
      const saved = unwrap(await api.DELETE("/api/milestones/{id}", { params: { path: { id: m.id } } }));
      useProjectStore.getState().applyMilestone(saved);
      await useProjectStore.getState().refreshDelta(d.project.id);
      onClose();
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };
  return (
    <div>
      <div className="flex items-start gap-2">
        <Flag className="mt-1 h-4 w-4" style={{ color: STATUS_COLOR[s] }} />
        <div className="min-w-0 flex-1">
          <h2 className="text-[16px] font-semibold">{m.name}</h2>
          <div className="text-[12px] text-muted">
            {m.start_date ? `${m.start_date} → ` : ""}{new Date(m.target_date + "T00:00:00").toLocaleDateString(i18n.language, { year: "numeric", month: "short", day: "numeric" })} · {t(`milestone.status.${s}`)} {dday}
          </div>
        </div>
        <button className="btn btn-ghost h-7 w-7 justify-center p-0" onClick={onClose}><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-3">
        <div className="mb-1 flex justify-between text-[12px] text-muted"><span>{t("task.progress")}</span><span>{prog ? `${Math.round(prog.ratio * 100)}%` : "—"}</span></div>
        <div className="h-1.5 overflow-hidden rounded-full bg-active"><div className="h-full rounded-full" style={{ width: `${Math.round((prog?.ratio ?? 0) * 100)}%`, background: STATUS_COLOR[s] }} /></div>
        {prog && <div className="mt-1 text-[11.5px] text-faint">{t("task.progressDetail", { done: prog.completed, started: prog.started, total: prog.total })}</div>}
      </div>
      {m.description && <div className="mt-3"><Markdown source={m.description} /></div>}
      {prog && prog.completed === prog.total && !m.completed_at && (
        <div className="mt-3 flex items-center justify-between rounded-md border border-success/40 bg-success/10 px-3 py-2 text-[12.5px]">
          <span>{t("milestone.allDone")}</span>
          <button className="btn h-7 text-[12px]" onClick={() => void complete(true)}>{t("milestone.markComplete")}</button>
        </div>
      )}
      <h3 className="mt-5 mb-1.5 text-[12px] font-semibold text-muted">{t("nav.tasks")} · {tasks.length}</h3>
      <ul className="divide-y divide-border/60 rounded-lg border border-border">
        {tasks.map((tk) => {
          const p = taskProgress(d, tk);
          const late = !isDone(tk) && tk.target_date && tk.target_date > m.target_date;
          return (
            <li key={tk.id} className="flex h-9 items-center gap-2 px-2 text-[13px]">
              <TaskStatusIcon status={tk.status} />
              <button className="min-w-0 flex-1 truncate text-left hover:underline" onClick={() => onOpenTask(tk.id)}>{tk.name}</button>
              {late && <span className="text-warning" title={t("milestone.taskLate")}>⚠</span>}
              <span className="text-[11.5px] text-muted">{p ? `${Math.round(p.ratio * 100)}%` : "—"}</span>
              <Avatar user={tk.lead_id ? d.users[tk.lead_id] : undefined} size={16} />
            </li>
          );
        })}
        {tasks.length === 0 && <li className="px-2 py-2 text-[12px] text-faint">{t("milestone.noTasks")}</li>}
      </ul>
      {canEdit && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          <button className="btn h-7 text-[12.5px]" onClick={onEdit}>{t("common.edit")}</button>
          {m.completed_at ? (
            <button className="btn h-7 text-[12.5px]" onClick={() => void complete(false)}>{t("milestone.reopen")}</button>
          ) : (
            <button className="btn h-7 text-[12.5px]" onClick={() => void complete(true)}><Check className="h-3.5 w-3.5" /> {t("milestone.markComplete")}</button>
          )}
          <button className="btn btn-ghost h-7 text-[12.5px] text-danger" onClick={() => void remove()}><Trash2 className="h-3.5 w-3.5" /> {t("common.delete")}</button>
        </div>
      )}
    </div>
  );
}

function MilestoneDialog({ d, target, onClose }: { d: ProjectData; target: Milestone | "new" | null; onClose: () => void }) {
  return (
    <Dialog.Root open={!!target} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        {target && <MilestoneForm d={d} m={target === "new" ? null : target} onClose={onClose} />}
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function MilestoneForm({ d, m, onClose }: { d: ProjectData; m: Milestone | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(m?.name ?? "");
  const [description, setDescription] = useState(m?.description ?? "");
  const [start, setStart] = useState(m?.start_date ?? "");
  const [target, setTarget] = useState(m?.target_date ?? "");
  const [busy, setBusy] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const submit = async () => {
    if (!name.trim() || !target || busy || editorBusy) return;
    setBusy(true);
    try {
      const saved = m
        ? unwrap(await api.PATCH("/api/milestones/{id}", { params: { path: { id: m.id } }, body: { name: name.trim(), description, start_date: start || null, target_date: target } }))
        : unwrap(await api.POST("/api/workspaces/{slug}/projects/{key}/milestones", { params: { path: { slug: d.slug, key: d.project.key } }, body: { name: name.trim(), description, start_date: start || null, target_date: target } }));
      useProjectStore.getState().applyMilestone(saved);
      onClose();
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog.Content className="fixed left-1/2 top-[16%] z-50 w-[520px] max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-xl border border-border bg-elevated p-5 shadow-2xl" onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && void submit()}>
      <div className="flex items-center justify-between">
        <Dialog.Title className="text-[15px] font-semibold">{m ? t("milestone.edit") : t("milestone.new")}</Dialog.Title>
        <Dialog.Close className="rounded p-1 text-muted hover:bg-hover"><X className="h-4 w-4" /></Dialog.Close>
      </div>
      <div className="mt-4 flex flex-col gap-3">
        <input autoFocus className="input h-9" placeholder={t("milestone.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} />
        <div className="flex gap-2">
          <DatePicker label={t("task.startDate")} value={start} onChange={setStart} boundary="start" max={target} disabled={busy} />
          <DatePicker label={t("task.targetDate")} value={target} onChange={setTarget} boundary="end" min={start} required disabled={busy} />
        </div>
        <MarkdownEditor onBusyChange={setEditorBusy} projectId={d.project.id} value={description} onChange={setDescription} placeholder={t("milestone.descriptionPlaceholder")} minRows={3} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Dialog.Close className="btn h-8">{t("common.cancel")}</Dialog.Close>
        <button className="btn btn-primary h-8" disabled={!name.trim() || !target || busy || editorBusy} onClick={() => void submit()}>{m ? t("common.save") : t("common.create")}</button>
      </div>
    </Dialog.Content>
  );
}
